import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Shield, Clock, CheckCircle2, ChevronRight, AlertTriangle } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { getAllAgreements, getProviderProfile } from '../lib/persistence';
import { Agreement, CATEGORY_CONFIG, ProviderProfile } from '../types';
import { getConnectUrl } from '../lib/mercadopago';
export function Dashboard() {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [profile, setProfile] = useState<ProviderProfile | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setProfile(getProviderProfile());
    getAllAgreements().then(data => {
      setAgreements(data);
      setLoading(false);
    });
  }, []);
  const formatCurrency = (amount: number, currency: string) => new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency
  }).format(amount);
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="w-4 h-4 text-yellow-500" data-id="element-355" />;
      case 'active':
        return <Shield className="w-4 h-4 text-blue-500" data-id="element-356" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-357" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" data-id="element-358" />;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending_payment':
        return 'Esperando pago';
      case 'active':
        return 'Fondos protegidos';
      case 'outcome_registered':
        return 'Esperando confirmación';
      case 'completed':
        return 'Completado';
      default:
        return status;
    }
  };
  const handleConnectMp = () => {
    if (!profile) return;
    // In a real app, we redirect to the MP OAuth URL
    // For this prototype, we'll just redirect to our simulated callback
    const url = getConnectUrl(profile.id);
    // window.location.href = url // Real behavior
    // Simulated behavior for prototype:
    window.location.href = `/mp/callback?code=simulated_auth_code_123&state=${btoa(JSON.stringify({
      providerId: profile.id
    }))}`;
  };
  return <Layout data-id="element-359">
      <div className="max-w-5xl mx-auto px-4 py-12" data-id="element-360">
        {profile && !profile.mp_connected && <div className="mb-8 bg-blue-50 border border-blue-200 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-id="element-361">
            <div className="flex items-start gap-4" data-id="element-362">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1 sm:mt-0" data-id="element-363">
                <AlertTriangle className="w-5 h-5 text-blue-600" data-id="element-364" />
              </div>
              <div data-id="element-365">
                <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-366">
                  Conectá tu cuenta para cobrar
                </h3>
                <p className="text-sm text-gray-600 max-w-xl" data-id="element-367">
                  Agreexa usa el modelo <strong data-id="element-368">Split de Mercado Pago</strong>.
                  El dinero va directo a tu cuenta y nosotros descontamos el 5%
                  automáticamente. No retenemos tus fondos.
                </p>
                <p className="text-xs text-blue-600 font-medium mt-2" data-id="element-369">
                  (Modo Demo: Hacé clic para simular la conexión OAuth)
                </p>
              </div>
            </div>
            <Button onClick={handleConnectMp} className="flex-shrink-0 whitespace-nowrap" data-id="element-370">
              Conectar Mercado Pago
            </Button>
          </div>}

        {profile && profile.mp_connected && <div className="mb-8 bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3" data-id="element-371">
            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-372" />
            <p className="text-sm text-green-800" data-id="element-373">
              <strong data-id="element-374">Cuenta conectada.</strong> Los pagos irán directo a tu
              cuenta de Mercado Pago (ID: {profile.mp_user_id}).
            </p>
          </div>}

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-375">
          <div data-id="element-376">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-377">
              Mis acuerdos
            </h1>
            <p className="text-gray-500" data-id="element-378">
              Gestioná tus servicios y pagos protegidos.
            </p>
          </div>
          <Link to="/create" data-id="element-379">
            <Button icon={<Plus className="w-4 h-4" data-id="element-381" />} data-id="element-380">Nuevo acuerdo</Button>
          </Link>
        </div>

        {loading ? <div className="flex justify-center py-12" data-id="element-382">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" data-id="element-383" />
          </div> : agreements.length === 0 ? <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 shadow-sm" data-id="element-384">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-385">
              <Shield className="w-8 h-8 text-gray-400" data-id="element-386" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-387">
              No tenés acuerdos todavía
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-388">
              Creá tu primer acuerdo para proteger tus servicios y asegurar tus
              cobros.
            </p>
            <Link to="/create" data-id="element-389">
              <Button data-id="element-390">Crear mi primer acuerdo</Button>
            </Link>
          </div> : <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-391">
            <div className="divide-y divide-gray-100" data-id="element-392">
              {agreements.map(agreement => <Link key={agreement.id} to={`/agreement/${agreement.agreement_code}/detail`} className="flex items-center gap-4 p-4 sm:p-6 hover:bg-gray-50 transition-colors group" data-id="element-393">
                  <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-xl flex-shrink-0 group-hover:bg-white transition-colors border border-transparent group-hover:border-gray-100" data-id="element-394">
                    {CATEGORY_CONFIG[agreement.category]?.icon || '✨'}
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-395">
                    <h4 className="text-base font-bold text-gray-900 truncate mb-1" data-id="element-396">
                      {agreement.title}
                    </h4>
                    <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-397">
                      <span className="truncate" data-id="element-398">
                        {agreement.client_name || agreement.client_email}
                      </span>
                      <span className="hidden sm:inline" data-id="element-399">•</span>
                      <span className="hidden sm:inline font-mono text-xs" data-id="element-400">
                        {agreement.agreement_code}
                      </span>
                    </div>
                  </div>

                  <div className="text-right flex-shrink-0" data-id="element-401">
                    <p className="text-base font-bold text-gray-900 mb-1" data-id="element-402">
                      {formatCurrency(agreement.amount, agreement.currency)}
                    </p>
                    <div className="flex items-center justify-end gap-1.5 text-xs font-medium text-gray-600" data-id="element-403">
                      {getStatusIcon(agreement.status)}
                      <span className="hidden sm:inline" data-id="element-404">
                        {getStatusText(agreement.status)}
                      </span>
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0 ml-2" data-id="element-405" />
                </Link>)}
            </div>
          </div>}
      </div>
    </Layout>;
}